import React, { useState } from 'react';
import { BPForm } from '@common/BPForm';
import { AuthField } from './Common/AuthField';
import { AuthCheckbox } from './Common/AuthCheckbox';
import { AuthLayout } from './Common/AuthLayout';
import { Button, Divider, Form, Grid, Header, Input, Message, Segment } from 'semantic-ui-react';
import * as yup from 'yup';
import { authClient } from './authClient';
import { LoginRequest } from '@app/api2/api';
import { useTranslation2 } from '../Contexts/TranslationContext2';
import { useQuery } from '@tanstack/react-query';
import { isNullOrWhitespace } from '@common/Utils';
import { AuthLayoutRow } from './Common/AuthLayoutRow';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { BPButton } from '@/components/bp/bpbutton';

interface LoginFormValues {
    email: string;
    password: string;
    rememberMe: boolean;
}

interface LoginProps {
    onLoginSuccess?: () => void;
    showCredits?: boolean;
    requestAccessUrl?: string;
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess, showCredits, requestAccessUrl }) => {
    const [loginError, setLoginError] = useState<string>('');

    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('returnUrl') || '/';

    const { translations: t } = useTranslation2();

    const info = useQuery({
        queryKey: ['loginInfo'],
        queryFn: async () => await authClient.getAppInfo()
    });

    const initialValues: LoginFormValues = {
        email: '',
        password: '',
        rememberMe: false
    };

    const validationSchema: yup.SchemaOf<LoginFormValues> = yup.object().shape({
        email: yup.string()
            .email('Invalid email address')
            .required('Email is required'),
        password: yup.string()
            .required('Password is required'),
        rememberMe: yup.boolean().required()
    });

    const handleSubmit = async (values: LoginFormValues) => {
        setLoginError('');

        try {
            const request: LoginRequest = {
                email: values.email,
                password: values.password,
                rememberMe: values.rememberMe
            };

            const result = await authClient.login(request);

            if (result.success) {
                if (onLoginSuccess) {
                    onLoginSuccess();
                }
                window.location.assign(returnUrl);
            } else if (result.requiresTwoFactor) {
                window.location.assign(buildUrl(AppRoutes.Login2fa, { returnUrl: returnUrl, rememberMe: values.rememberMe }));
            } else if (result.isLockedOut) {
                window.location.assign(buildUrl(AppRoutes.Lockout, {}));
            } else {
                setLoginError(result.errorMessage || 'Invalid login attempt.');
            }
        } catch (error) {
            console.error('Login error:', error);
            setLoginError('An error occurred during login. Please try again.');
        }
    };

    if (info.isLoading) {
        return <div>{t.common.loading}...</div>;
    }

    return (
        <AuthLayout>
            <AuthLayoutRow>
                <Segment>
                    {loginError && (
                        <Message error>
                            {loginError}
                        </Message>
                    )}

                    <BPForm
                        initialValues={initialValues}
                        vaildationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        hideButtons={true}
                    >
                        {(formProps) => (
                            <>
                                <AuthField
                                    name="email"
                                    label={t.common.emailAddress}
                                    autoComplete="username"
                                    placeholder="name@example.com"
                                />

                                <AuthField
                                    name="password"
                                    label={t.common.password}
                                    type="password"
                                    autoComplete="current-password"
                                    placeholder={t.common.password}
                                />

                                {/* <AuthCheckbox
                                    name="rememberMe"
                                    label="Remember me?"
                                /> */}

                                <Divider />

                                <BPButton
                                    type="submit"
                                    primary
                                    className='bp-primary'
                                    size="large"
                                    fluid
                                    disabled={formProps.isSubmitting}
                                    loading={formProps.isSubmitting}
                                >
                                    {t.account.login}
                                </BPButton>
                            </>
                        )}
                    </BPForm>

                    <Segment basic textAlign="center">
                        <a href={buildUrl(AppRoutes.ForgotPassword, {})}>{t.account.forgotPassword}</a>
                        {!isNullOrWhitespace(info.data?.requestAccessUrl) && (
                            <>
                                <span> | </span>
                                <a href={`${info.data?.requestAccessUrl}`}>{t.account.requestAccess}</a>
                            </>)}
                    </Segment>
                </Segment>
            </AuthLayoutRow>
            {info.data.credits && <AuthLayoutRow centered>
                <div className="credits" dangerouslySetInnerHTML={{ __html: info.data?.credits || 'Credits' }} />
            </AuthLayoutRow>}
        </AuthLayout>
    );
};